import React from 'react';
import { Column, Row } from 'react-table';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import { useGetColumnConfig } from 'Components/Table/utils';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import { ListEntryApiResponseObj } from '@brighterion/types/listEntry';

type Props = {
    listType: SingleListType;
    merchantSpecific: boolean;
    updateModal: (row: Row<ListEntryApiResponseObj>, action: UpdateModalFunctionSchema['action']) => void;
    isInViewRemovedEntriesPage?: boolean;
};

const useGetListEntryTableColumns = ({
    listType,
    merchantSpecific,
    updateModal,
    isInViewRemovedEntriesPage = false
}: Props) => {
    const { getColConfig } = useGetColumnConfig<ListEntryApiResponseObj>();
    const { t } = useTranslation();

    return React.useMemo(() => {
        const defaultColumns: Column<ListEntryApiResponseObj>[] = [
            {
                Header: 'Start Date',
                id: '_formatted.startTime',
                accessor: 'startTime',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 100
            },
            {
                Header: 'End Date',
                id: '_formatted.endTime',
                accessor: 'endTime',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 100
            },
            {
                Header: isInViewRemovedEntriesPage ? 'Removed By' : 'Last Modified By',
                id: isInViewRemovedEntriesPage ? '_formatted.removedBy' : '_formatted.modifiedBy',
                accessor: (d) => (isInViewRemovedEntriesPage ? d?._formatted?.removedBy : d?._formatted?.modifiedBy),
                dataType: 'text',
                width: 100
            },
            {
                Header: isInViewRemovedEntriesPage ? 'Removed On' : 'Last Modified',
                id: isInViewRemovedEntriesPage ? '_formatted.removedAt' : '_formatted.modifiedAt',
                accessor: isInViewRemovedEntriesPage ? 'removedAt' : 'modifiedAt',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 100
            },
            {
                Header: 'Comment',
                width: 100,
                minWidth: 100,
                maxWidth: 100,
                accessor: 'comment',
                id: 'comment',
                renderType: 'commentIcon',
                Cell: (props) => (props.value ? props.value : '')
            }
        ];

        if (isInViewRemovedEntriesPage) {
            defaultColumns.push({
                Header: 'Status',
                id: '_formatted.status',
                accessor: (d) => d?._formatted?.status,
                width: 140,
                minWidth: 140,
                maxWidth: 140
            });
        }

        if (RoleService.hasListAccess && !isInViewRemovedEntriesPage) {
            defaultColumns.push({
                Header: () => null,
                accessor: 'id',
                id: 'modify-list-entry',
                disableSortBy: true,
                width: 40,
                minWidth: 40,
                renderType: 'overrideCell',
                Cell: ({ id, row }) => (
                    <RowActionDropdown
                        menuId={`${id}-action-dropdown`}
                        menuActions={
                            [
                                {
                                    testId: 'edit-list-entry',
                                    label: t('Edit'),
                                    handleAction: (event: React.MouseEvent | React.KeyboardEvent) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        updateModal(row, 'edit');
                                    }
                                },
                                {
                                    testId: 'delete-list-entry',
                                    label: t('Delete'),
                                    handleAction: (event: React.MouseEvent | React.KeyboardEvent) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        updateModal(row, 'delete');
                                    },
                                    style: { color: 'var(--red000)' }
                                }
                            ] as any
                        }
                    />
                )
            });
        }

        if (merchantSpecific) {
            defaultColumns.unshift({
                Header: 'Merchant',
                id: '_formatted.merchantId',
                width: 80,
                accessor: (d) => d?._formatted?.merchantId,
                dataType: 'text'
            });
        }

        switch (listType) {
            case 'GENERAL': {
                const generalListColumns: Column<ListEntryApiResponseObj>[] = [
                    {
                        Header: 'Value',
                        width: isInViewRemovedEntriesPage ? 150 : 300,
                        accessor: 'value',
                        id: 'value',
                        dataType: 'text'
                    },
                    ...defaultColumns
                ];
                return generalListColumns.map(getColConfig);
            }
            case 'PAN': {
                const panColumns: Column<ListEntryApiResponseObj>[] = [
                    {
                        Header: 'Primary Account Number',
                        width: isInViewRemovedEntriesPage ? 150 : 300,
                        accessor: 'value',
                        id: 'value',
                        renderType: 'bcatsMasked'
                    },
                    ...defaultColumns
                ];
                return panColumns.map(getColConfig);
            }
            case 'RANGE': {
                const rangeColumns: Column<ListEntryApiResponseObj>[] = [
                    {
                        Header: 'Range',
                        width: isInViewRemovedEntriesPage ? 150 : 300,
                        accessor: 'value',
                        id: 'value',
                        dataType: 'text'
                    },
                    ...defaultColumns
                ];
                return rangeColumns.map(getColConfig);
            }
            default:
                return defaultColumns.map(getColConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getColConfig, listType]);
};

export default useGetListEntryTableColumns;
